import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { getStates, getCitiesForState } from '../utils/locationData';
import { getImageUrl } from '../utils/imageUtils';

const EditListing = ({ listing, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    itemName: '',
    listingType: 'rent',
    itemType: '',
    category: '',
    clothingType: '',
    jewelleryType: '',
    handycraftType: '',
    color: '',
    size: '',
    jewellerySize: '',
    price: '',
    condition: '',
    state: '',
    city: '',
    fabric: '',
    material: '',
    metalPurity: '',
    description: ''
  });
  const [availableStates] = useState(getStates());
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (listing) {
      // Parse location to get state and city
      const locationParts = listing.location ? listing.location.split(', ') : ['', ''];
      const city = locationParts[0] || '';
      const state = locationParts[1] || '';

      setFormData({
        itemName: listing.itemName || '',
        listingType: listing.listingType || 'rent',
        itemType: listing.itemType || '',
        category: listing.category || '',
        clothingType: listing.clothingType || '',
        jewelleryType: listing.jewelleryType || '',
        handycraftType: listing.handycraftType || '',
        color: listing.color || '',
        size: listing.size || '',
        jewellerySize: listing.jewellerySize || '',
        price: listing.price || '',
        condition: listing.condition || '',
        state: state,
        city: city,
        fabric: listing.fabric || '',
        material: listing.material || '',
        metalPurity: listing.metalPurity || '',
        description: listing.description || ''
      });

      // Set available cities if state is available
      if (state) {
        setAvailableCities(getCitiesForState(state));
      }
    }
  }, [listing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'state') {
      // When state changes, update available cities and clear city selection
      setFormData({
        ...formData,
        state: value,
        city: '' // Clear city when state changes
      });
      setAvailableCities(getCitiesForState(value));
    } else if (name === 'itemType') {
      // When itemType changes, clear fields that are not relevant to the new type
      const updatedFormData = {
        ...formData,
        [name]: value
      };
      
      if (value === 'handycraft') {
        // Clear clothing and jewellery specific fields
        updatedFormData.category = '';
        updatedFormData.clothingType = '';
        updatedFormData.jewelleryType = '';
        updatedFormData.size = '';
        updatedFormData.jewellerySize = '';
        updatedFormData.fabric = '';
        updatedFormData.metalPurity = '';
      } else if (value === 'clothing') {
        // Clear jewellery specific fields
        updatedFormData.jewelleryType = '';
        updatedFormData.jewellerySize = '';
        updatedFormData.metalPurity = '';
        updatedFormData.handycraftType = '';
      } else if (value === 'jewellery') {
        // Clear clothing specific fields
        updatedFormData.clothingType = '';
        updatedFormData.size = '';
        updatedFormData.fabric = '';
        updatedFormData.handycraftType = '';
      }
      
      setFormData(updatedFormData);
    } else if (name === 'jewelleryType') {
      // Clear jewellerySize if not applicable for this type
      const updatedFormData = {
        ...formData,
        [name]: value
      };
      
      if (!['ring', 'bracelet', 'anklet', 'bangles'].includes(value)) {
        updatedFormData.jewellerySize = '';
      }
      
      setFormData(updatedFormData);
    } else if (name === 'material') {
      // Clear metalPurity if not applicable for this material
      const updatedFormData = {
        ...formData,
        [name]: value
      };
      
      if (!['gold', 'silver', 'platinum'].includes(value)) {
        updatedFormData.metalPurity = '';
      }
      
      setFormData(updatedFormData);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.state || !formData.city) {
      toast.error('Please select both state and city');
      return;
    }

    // Validate required fields based on item type
    if (formData.itemType === 'clothing') {
      if (!formData.clothingType) {
        toast.error('Please select a clothing type');
        return;
      }
      if (!formData.size) {
        toast.error('Please select a size for clothing items');
        return;
      }
      if (!formData.fabric) {
        toast.error('Please specify the fabric for clothing items');
        return;
      }
      if (!formData.category) {
        toast.error('Please select a category for clothing items');
        return;
      }
    }

    if (formData.itemType === 'jewellery') {
      if (!formData.jewelleryType) {
        toast.error('Please select a jewellery type');
        return;
      }
      if (!formData.material) {
        toast.error('Please specify the material for jewellery items');
        return;
      }
      if (!formData.category) {
        toast.error('Please select a category for jewellery items');
        return;
      }
      // Validate size requirements for specific jewellery types
      if (['ring', 'bracelet', 'anklet', 'bangles'].includes(formData.jewelleryType) && !formData.jewellerySize) {
        toast.error('Please select a size for this jewellery type');
        return;
      }
    }

    if (formData.itemType === 'handycraft') {
      if (!formData.handycraftType) {
        toast.error('Please select a handycraft type');
        return;
      }
      if (!formData.material) {
        toast.error('Please specify the material for handycraft items');
        return;
      }
    }

    setLoading(true);

    try {
      const updateData = {
        ...formData,
        location: `${formData.city}, ${formData.state}`
      };

      // Remove state and city from the data sent to backend
      delete updateData.state;
      delete updateData.city;

      // Clean up conditional fields based on item type
      if (updateData.itemType === 'handycraft') {
        // For handycraft items, remove clothing and jewellery specific fields
        delete updateData.category;
        delete updateData.clothingType;
        delete updateData.jewelleryType;
        delete updateData.size;
        delete updateData.jewellerySize;
        delete updateData.fabric;
        delete updateData.metalPurity;
      } else if (updateData.itemType === 'clothing') {
        // For clothing items, remove jewellery specific fields
        delete updateData.jewelleryType;
        delete updateData.jewellerySize;
        delete updateData.metalPurity;
        // Keep material field empty for clothing as it's not required
        delete updateData.material;
      } else if (updateData.itemType === 'jewellery') {
        // For jewellery items, remove clothing specific fields
        delete updateData.clothingType;
        delete updateData.size;
        delete updateData.fabric;
        // Remove jewellerySize if not applicable for this jewellery type
        if (!['ring', 'bracelet', 'anklet', 'bangles'].includes(updateData.jewelleryType)) {
          delete updateData.jewellerySize;
        }
        // Remove metalPurity if not applicable for this material
        if (!['gold', 'silver', 'platinum'].includes(updateData.material)) {
          delete updateData.metalPurity;
        }
      }

      // Remove empty string values for optional fields to avoid enum validation errors
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          delete updateData[key];
        }
      });

      const response = await api.put(`/listings/${listing._id}`, updateData);

      if (response.data) {
        toast.success('Listing updated successfully!');
        onUpdate(response.data.listing);
        onClose();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error updating listing';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!listing) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-start sm:items-center justify-center overflow-y-auto"
      onClick={handleBackdropClick}
      style={{ 
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      <div className="bg-white rounded-none sm:rounded-2xl max-w-4xl w-full min-h-screen sm:min-h-0 sm:max-h-[95vh] overflow-y-auto sm:my-4 flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:rounded-t-2xl z-10 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#222831' }}>
              <i className="fas fa-edit mr-2" style={{ color: '#948979' }}></i>
              Edit Listing
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl p-2 -mr-2 touch-manipulation"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <p className="text-sm mt-2" style={{ color: '#393E46' }}>
            Update your listing details. Note: Images cannot be changed.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="flex-1 p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Item Name */}
              <div className="md:col-span-2">
                <label htmlFor="itemName" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Item Name *
                </label>
                <input
                  type="text"
                  id="itemName"
                  name="itemName"
                  required
                  value={formData.itemName}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder="e.g., Designer Lehenga, Diamond Necklace, Formal Suit"
                />
              </div>

              {/* Listing Type */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Listing Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, listingType: 'rent' })}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${formData.listingType === 'rent'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <i className={`fas fa-handshake text-2xl ${formData.listingType === 'rent' ? 'text-green-600' : 'text-gray-400'
                        }`}></i>
                    </div>
                    <div className="text-center">
                      <h4 className={`font-semibold ${formData.listingType === 'rent' ? 'text-green-700' : 'text-gray-700'
                        }`}>Rent</h4>
                      <p className="text-xs text-gray-500 mt-1">Lend your item for a daily fee</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, listingType: 'sell' })}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${formData.listingType === 'sell'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <i className={`fas fa-tag text-2xl ${formData.listingType === 'sell' ? 'text-blue-600' : 'text-gray-400'
                        }`}></i>
                    </div>
                    <div className="text-center">
                      <h4 className={`font-semibold ${formData.listingType === 'sell' ? 'text-blue-700' : 'text-gray-700'
                        }`}>Sell</h4>
                      <p className="text-xs text-gray-500 mt-1">Sell your item permanently</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Item Type */}
              <div>
                <label htmlFor="itemType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Item Type *
                </label>
                <select
                  id="itemType"
                  name="itemType"
                  required
                  value={formData.itemType}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select Item Type</option>
                  <option value="clothing">Clothing</option>
                  <option value="jewellery">Jewellery</option>
                  <option value="handycraft">Handy Craft</option>
                </select>
              </div>

              {/* Category - Only show for clothing and jewellery */}
              {(formData.itemType === 'clothing' || formData.itemType === 'jewellery') && (
                <div>
                  <label htmlFor="category" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Category</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>
              )}

              {/* Clothing Type */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="clothingType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Clothing Type *
                  </label>
                  <select
                    id="clothingType"
                    name="clothingType"
                    required
                    value={formData.clothingType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="ethnic">Ethnic</option>
                    <option value="western">Western</option>
                    <option value="casual">Casual</option>
                    <option value="formal">Formal</option>
                    <option value="party">Party</option>
                    <option value="traditional">Traditional</option>
                  </select>
                </div>
              )}

              {/* Jewellery Type */}
              {formData.itemType === 'jewellery' && (
                <div>
                  <label htmlFor="jewelleryType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Jewellery Type *
                  </label>
                  <select
                    id="jewelleryType"
                    name="jewelleryType"
                    required
                    value={formData.jewelleryType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="necklace">Necklace</option>
                    <option value="earrings">Earrings</option>
                    <option value="bracelet">Bracelet</option>
                    <option value="ring">Ring</option>
                    <option value="anklet">Anklet</option>
                    <option value="bangles">Bangles</option>
                    <option value="chain">Chain</option>
                    <option value="pendant">Pendant</option>
                    <option value="brooch">Brooch</option>
                    <option value="cufflinks">Cufflinks</option>
                    <option value="watch">Watch</option>
                  </select>
                </div>
              )}

              {/* Handycraft Type */}
              {formData.itemType === 'handycraft' && (
                <div>
                  <label htmlFor="handycraftType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Handycraft Type *
                  </label>
                  <select
                    id="handycraftType"
                    name="handycraftType"
                    required
                    value={formData.handycraftType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="pottery">Pottery</option>
                    <option value="woodwork">Woodwork</option>
                    <option value="textiles">Textiles</option>
                    <option value="paintings">Paintings</option>
                    <option value="sculptures">Sculptures</option>
                    <option value="candles">Candles</option>
                    <option value="soaps">Handmade Soaps</option>
                    <option value="bags">Handmade Bags</option>
                    <option value="decorative">Decorative Items</option>
                    <option value="toys">Handmade Toys</option>
                    <option value="accessories">Craft Accessories</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              {/* Color */}
              <div>
                <label htmlFor="color" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Color *
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  required
                  value={formData.color}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder="e.g., Red, Blue, Multi-color"
                />
              </div>

              {/* Size - Only show for clothing */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="size" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Size *
                  </label>
                  <select
                    id="size"
                    name="size"
                    required
                    value={formData.size}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="28">28</option>
                    <option value="30">30</option>
                    <option value="32">32</option>
                    <option value="34">34</option>
                    <option value="36">36</option>
                    <option value="38">38</option>
                    <option value="40">40</option>
                    <option value="42">42</option>
                  </select>
                </div>
              )}

              {/* Jewellery Size */}
              {formData.itemType === 'jewellery' && ['ring', 'bracelet', 'anklet', 'bangles'].includes(formData.jewelleryType) && (
                <div>
                  <label htmlFor="jewellerySize" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Size *
                  </label>
                  <select
                    id="jewellerySize"
                    name="jewellerySize"
                    required
                    value={formData.jewellerySize}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="Free Size">Free Size</option>
                    {formData.jewelleryType === 'ring' && (
                      <>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                        <option value="9">9</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  {formData.listingType === 'rent' ? 'Rental Price (₹/day) *' : 'Sale Price (₹) *'}
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  required
                  min="1"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder={formData.listingType === 'rent' ? 'Enter price per day' : 'Enter sale price'}
                />
              </div>

              {/* Condition */}
              <div>
                <label htmlFor="condition" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Condition *
                </label>
                <select
                  id="condition"
                  name="condition"
                  required
                  value={formData.condition}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select Condition</option>
                  <option value="excellent">Excellent - Like new</option>
                  <option value="good">Good - Minor wear</option>
                  <option value="fair">Fair - Visible wear but functional</option>
                </select>
              </div>

              {/* State */}
              <div>
                <label htmlFor="state" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  State *
                </label>
                <select
                  id="state"
                  name="state"
                  required
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select State</option>
                  {availableStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  City *
                </label>
                <select
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  disabled={!formData.state}
                >
                  <option value="">
                    {formData.state ? 'Select City' : 'Select State First'}
                  </option>
                  {availableCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Fabric - Only show for clothing */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="fabric" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Fabric *
                  </label>
                  <input
                    type="text"
                    id="fabric"
                    name="fabric"
                    required
                    value={formData.fabric}
                    onChange={handleInputChange}
                    className="form-input w-full"
                    placeholder="e.g., Cotton, Silk, Polyester"
                  />
                </div>
              )}

              {/* Material - Only show for jewellery */}
              {formData.itemType === 'jewellery' && (
                <div>
                  <label htmlFor="material" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Material *
                  </label>
                  <select
                    id="material"
                    name="material"
                    required
                    value={formData.material}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Material</option>
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="platinum">Platinum</option>
                    <option value="diamond">Diamond</option>
                    <option value="pearl">Pearl</option>
                    <option value="gemstone">Gemstone</option>
                    <option value="artificial">Artificial</option>
                    <option value="brass">Brass</option>
                    <option value="copper">Copper</option>
                    <option value="stainless-steel">Stainless Steel</option>
                  </select>
                </div>
              )}

              {/* Material - Only show for handy craft */}
              {formData.itemType === 'handycraft' && (
                <div>
                  <label htmlFor="material" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Material *
                  </label>
                  <select
                    id="material"
                    name="material"
                    required
                    value={formData.material}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Material</option>
                    <option value="wood">Wood</option>
                    <option value="clay">Clay</option>
                    <option value="fabric">Fabric</option>
                    <option value="paper">Paper</option>
                    <option value="metal">Metal</option>
                    <option value="glass">Glass</option>
                    <option value="plastic">Plastic</option>
                    <option value="stone">Stone</option>
                    <option value="leather">Leather</option>
                    <option value="bamboo">Bamboo</option>
                    <option value="ceramic">Ceramic</option>
                    <option value="resin">Resin</option>
                    <option value="mixed">Mixed Materials</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              {/* Metal Purity - Only show for precious metals */}
              {formData.itemType === 'jewellery' && ['gold', 'silver', 'platinum'].includes(formData.material) && (
                <div>
                  <label htmlFor="metalPurity" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Metal Purity (Optional)
                  </label>
                  <select
                    id="metalPurity"
                    name="metalPurity"
                    value={formData.metalPurity}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Purity</option>
                    {formData.material === 'gold' && (
                      <>
                        <option value="14k">14K</option>
                        <option value="18k">18K</option>
                        <option value="22k">22K</option>
                        <option value="24k">24K</option>
                      </>
                    )}
                    {formData.material === 'silver' && (
                      <option value="925-silver">925 Silver</option>
                    )}
                    <option value="not-applicable">Not Applicable</option>
                  </select>
                </div>
              )}

              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder={formData.itemType === 'jewellery'
                    ? "Describe your jewellery - design, occasion, care instructions, authenticity, etc."
                    : "Describe your item in detail - style, fit, occasion, care instructions, etc."
                  }
                />
              </div>

              {/* Images Info */}
              <div className="md:col-span-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="fas fa-info-circle text-amber-600 mt-1 mr-3"></i>
                    <div>
                      <h4 className="font-medium text-amber-800 mb-1">Images Cannot Be Changed</h4>
                      <p className="text-sm text-amber-700">
                        For security and authenticity reasons, listing images cannot be modified after creation. 
                        All other details can be updated as needed.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Show current images */}
                {listing.images && listing.images.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                      Current Images
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {listing.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={getImageUrl(image)}
                            alt={`${listing.itemName} ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZTwvdGV4dD48L3N2Zz4=';
                            }}
                          />
                          <div className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t border-gray-200 sticky bottom-0 bg-white px-4 sm:px-6 pb-4 sm:pb-6 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 rounded-lg text-white transition-colors flex items-center justify-center touch-manipulation"
              style={{ 
                backgroundColor: loading ? '#9CA3AF' : '#948979',
                minHeight: '44px'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#393E46')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#948979')}
            >
              {loading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin mr-2"></i>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Update Listing
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditListing;